import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import GameScoreboard from './components/GameScoreboard';
import PlayerRoster from './components/PlayerRoster';
import GameEventLog from './components/GameEventLog';
import StatisticsPanel from './components/StatisticsPanel';
import QuickActions from './components/QuickActions';
import PreviousRecordedGames from './components/PreviousRecordedGames';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { matchService } from '../../services/matchService';
import { tournamentService } from '../../services/tournamentService';
import { playerService } from '../../services/playerService';
import { regionalTeamRosterService } from '../../services/regionalTeamRosterService';

const MATCH_TYPE_OPTIONS = [
  { value: 'league', label: 'League Match' },
  { value: 'tournament', label: 'Tournament Match' },
];

const PERIOD_LENGTH = 600; // 10 minutes per quarter
const SYNC_DEBOUNCE_MS = 800;

const isOvertimePeriod = (period) => (Number(period) || 0) >= 5;

const formatPeriodLabel = (period) => {
  const p = Number(period) || 1;
  if (p <= 4) return `Q${p}`;
  return p === 5 ? 'OT' : `OT${p - 4}`;
};

const getClockForPeriod = (period) => (isOvertimePeriod(period) ? null : PERIOD_LENGTH);

const ensureQuarterScores = (scores = [], period = 1) => {
  const next = Array.isArray(scores) ? [...scores] : [0, 0, 0, 0];
  while (next.length < Math.max(4, period)) next.push(0);
  return next;
};

const formatScheduleLabel = (dateValue) => {
  if (!dateValue) return 'Schedule TBD';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Schedule TBD';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const makeShortName = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'TM';

const formatClock = (totalSeconds = 0) => {
  if (totalSeconds == null) return 'OT';
  const secs = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${String(mins).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
};

const parseClock = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.includes(':')) {
    const [m, s] = value.split(':').map((part) => parseInt(part, 10));
    return (Number.isFinite(m) ? m : 0) * 60 + (Number.isFinite(s) ? s : 0);
  }
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : PERIOD_LENGTH;
};

const emptyPlayerStats = () => ({
  points: 0,
  rebounds: 0,
  assists: 0,
  fouls: 0,
  steals: 0,
  blocks: 0,
  turnovers: 0,
  fieldGoalsMade: 0,
  fieldGoalsAttempted: 0,
  threePointersMade: 0,
  threePointersAttempted: 0,
  freeThrowsMade: 0,
  freeThrowsAttempted: 0,
  playingTime: 0,
});

const mapLeaguePlayerToRoster = (player, index) => ({
  id: player.id,
  name: player.userProfile?.fullName || 'Player',
  jerseyNumber: player.jerseyNumber ?? index + 1,
  position: player.playerPosition || '—',
  isActive: index < 5,
  ...emptyPlayerStats(),
});

const mapRegionalPlayerToRoster = (player, index) => ({
  id: player.id,
  name: player.fullName || 'Player',
  jerseyNumber: player.jerseyNumber ?? index + 1,
  position: player.position || '—',
  isActive: index < 5,
  ...emptyPlayerStats(),
});

const aggregateTeamStats = (players = []) =>
  players.reduce(
    (acc, p) => ({
      points: acc.points + (p.points || 0),
      fieldGoalsMade: acc.fieldGoalsMade + (p.fieldGoalsMade || 0),
      fieldGoalsAttempted: acc.fieldGoalsAttempted + (p.fieldGoalsAttempted || 0),
      threePointersMade: acc.threePointersMade + (p.threePointersMade || 0),
      threePointersAttempted: acc.threePointersAttempted + (p.threePointersAttempted || 0),
      freeThrowsMade: acc.freeThrowsMade + (p.freeThrowsMade || 0),
      freeThrowsAttempted: acc.freeThrowsAttempted + (p.freeThrowsAttempted || 0),
      totalRebounds: acc.totalRebounds + (p.rebounds || 0),
      assists: acc.assists + (p.assists || 0),
      steals: acc.steals + (p.steals || 0),
      blocks: acc.blocks + (p.blocks || 0),
      turnovers: acc.turnovers + (p.turnovers || 0),
      fouls: acc.fouls + (p.fouls || 0),
    }),
    {
      points: 0,
      fieldGoalsMade: 0,
      fieldGoalsAttempted: 0,
      threePointersMade: 0,
      threePointersAttempted: 0,
      freeThrowsMade: 0,
      freeThrowsAttempted: 0,
      totalRebounds: 0,
      assists: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      fouls: 0,
    }
  );

const toLiveMatchShape = (game, matchType) => {
  if (!game) return null;

  if (matchType === 'tournament') {
    const homeName = game.homeTeam?.teamName || 'Home Team';
    const awayName = game.awayTeam?.teamName || 'Away Team';
    const isFinal = game.gameStatus === 'completed';
    return {
      id: game.id,
      matchType,
      leagueId: null,
      source: game,
      homeTeam: {
        id: game.homeTeamId || game.homeTeam?.id,
        name: homeName,
        shortName: makeShortName(homeName),
        score: game.homeScore || 0,
        timeouts: 3,
        teamFouls: 0,
        quarterScores: [0, 0, 0, 0],
      },
      awayTeam: {
        id: game.awayTeamId || game.awayTeam?.id,
        name: awayName,
        shortName: makeShortName(awayName),
        score: game.awayScore || 0,
        timeouts: 3,
        teamFouls: 0,
        quarterScores: [0, 0, 0, 0],
      },
      gameState: {
        status: game.gameStatus === 'live' ? 'live' : isFinal ? 'final' : 'scheduled',
        period: 1,
        timeRemaining: PERIOD_LENGTH,
        venue: game.venue || 'TBD',
        date: formatScheduleLabel(game.scheduledDate),
        officials: [],
      },
    };
  }

  const homeName = game.homeTeam?.name || 'Home Team';
  const awayName = game.awayTeam?.name || 'Away Team';
  const isFinal = game.matchStatus === 'completed';
  return {
    id: game.id,
    matchType,
    leagueId: game.leagueId,
    source: game,
    homeTeam: {
      id: game.homeTeamId || game.homeTeam?.id,
      name: homeName,
      shortName: game.homeTeam?.shortName || makeShortName(homeName),
      score: game.homeScore || 0,
      timeouts: 3,
      teamFouls: 0,
      quarterScores: [0, 0, 0, 0],
    },
    awayTeam: {
      id: game.awayTeamId || game.awayTeam?.id,
      name: awayName,
      shortName: game.awayTeam?.shortName || makeShortName(awayName),
      score: game.awayScore || 0,
      timeouts: 3,
      teamFouls: 0,
      quarterScores: [0, 0, 0, 0],
    },
    gameState: {
      status: game.matchStatus === 'live' ? 'live' : isFinal ? 'final' : 'scheduled',
      period: game.quarter || 1,
      timeRemaining: parseClock(game.timeRemaining),
      venue: game.venue || 'TBD',
      date: formatScheduleLabel(game.scheduledDate),
      officials: [game.referee?.fullName, game.umpire1?.fullName, game.umpire2?.fullName].filter(
        Boolean
      ),
    },
  };
};

const MatchManagement = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [matchType, setMatchType] = useState('league');
  const [scheduledGames, setScheduledGames] = useState([]);
  const [recordedGames, setRecordedGames] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [playersByTeam, setPlayersByTeam] = useState({});
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [isLoadingRecorded, setIsLoadingRecorded] = useState(false);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  const [recordedError, setRecordedError] = useState(null);
  const [banner, setBanner] = useState(null);
  const [gameEvents, setGameEvents] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);

  const syncTimerRef = useRef(null);
  const startedAtRef = useRef(null);
  const selectedMatchRef = useRef(null);
  const playersByTeamRef = useRef({});

  const userRole = userProfile?.role;
  const hasAccess = userRole === 'admin' || userRole === 'staff' || userRole === 'official';
  const isFinal = selectedMatch?.gameState?.status === 'final';

  useEffect(() => {
    selectedMatchRef.current = selectedMatch;
  }, [selectedMatch]);

  useEffect(() => {
    playersByTeamRef.current = playersByTeam;
  }, [playersByTeam]);

  const showBanner = useCallback((type, message) => {
    setBanner({ type, message });
    window.setTimeout(() => setBanner(null), 5000);
  }, []);

  const pushEvent = useCallback((partial) => {
    const match = selectedMatchRef.current;
    if (!match) return;
    const event = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
      period: match.gameState.period,
      timeRemaining: match.gameState.timeRemaining,
      ...partial,
    };
    setGameEvents((prev) => [event, ...prev]);
  }, []);

  const toBackendStatus = useCallback((status, started) => {
    if (status === 'final') return 'completed';
    if (status === 'live') return 'live';
    return started ? 'live' : 'scheduled';
  }, []);

  const syncScores = useCallback(
    async (matchOverride = null, extra = {}) => {
      const match = matchOverride || selectedMatchRef.current;
      if (!match || match.gameState.status === 'final') return;

      const payloadStatus = toBackendStatus(match.gameState.status, !!startedAtRef.current);
      const timeRemaining = formatClock(match.gameState.timeRemaining);

      try {
        if (match.matchType === 'league') {
          await matchService.updateScore(match.id, {
            homeScore: match.homeTeam.score,
            awayScore: match.awayTeam.score,
            quarter: match.gameState.period,
            timeRemaining,
            matchStatus: extra.matchStatus || payloadStatus,
            startedAt: extra.startedAt,
            endedAt: extra.endedAt,
          });
        } else {
          await tournamentService.updateGame(match.id, {
            homeScore: match.homeTeam.score,
            awayScore: match.awayTeam.score,
            gameStatus: extra.gameStatus || payloadStatus,
          });
        }
      } catch (err) {
        console.error('Failed to sync match score:', err);
        showBanner('error', err.message || 'Failed to sync score to server.');
      }
    },
    [showBanner, toBackendStatus]
  );

  const scheduleSync = useCallback(() => {
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(() => {
      syncScores();
    }, SYNC_DEBOUNCE_MS);
  }, [syncScores]);

  useEffect(() => {
    if (authLoading) return;
    if (!hasAccess) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [authLoading, hasAccess, navigate]);

  useEffect(() => {
    if (!hasAccess) return;

    const loadScheduledGames = async () => {
      setIsLoadingGames(true);
      setGamesError(null);
      setSelectedMatchId('');
      setSelectedMatch(null);
      setPlayersByTeam({});
      setGameEvents([]);
      setHasStarted(false);
      startedAtRef.current = null;

      try {
        if (matchType === 'tournament') {
          const games = await tournamentService.getGames();
          const sorted = (games || [])
            .filter((g) => ['scheduled', 'live'].includes(g.gameStatus) || !g.gameStatus)
            .slice()
            .sort((a, b) => new Date(a.scheduledDate || 0) - new Date(b.scheduledDate || 0));
          setScheduledGames(sorted);
        } else {
          const matches = await matchService.getAll();
          const sorted = (matches || [])
            .filter(
              (match) =>
                ['scheduled', 'live', 'halftime'].includes(match.matchStatus) || !match.matchStatus
            )
            .slice()
            .sort((a, b) => new Date(a.scheduledDate || 0) - new Date(b.scheduledDate || 0));
          setScheduledGames(sorted);
        }
      } catch (err) {
        console.error('Failed to load scheduled games:', err);
        setGamesError(err.message || 'Failed to load scheduled games.');
        setScheduledGames([]);
      } finally {
        setIsLoadingGames(false);
      }
    };

    loadScheduledGames();
  }, [matchType, hasAccess]);

  const loadRecordedGames = useCallback(async () => {
    if (!hasAccess) return;
    setIsLoadingRecorded(true);
    setRecordedError(null);
    try {
      if (matchType === 'tournament') {
        const games = await tournamentService.getGames();
        const completed = (games || [])
          .filter(
            (g) =>
              g.gameStatus === 'completed' ||
              (g.homeScore != null && g.awayScore != null)
          )
          .slice()
          .sort(
            (a, b) =>
              new Date(b.scheduledDate || b.createdAt || 0) -
              new Date(a.scheduledDate || a.createdAt || 0)
          );
        setRecordedGames(completed);
      } else {
        const matches = await matchService.getAll();
        const completed = (matches || [])
          .filter(
            (m) =>
              m.matchStatus === 'completed' ||
              (m.homeScore != null && m.awayScore != null)
          )
          .slice()
          .sort(
            (a, b) =>
              new Date(b.endedAt || b.scheduledDate || 0) -
              new Date(a.endedAt || a.scheduledDate || 0)
          );
        setRecordedGames(completed);
      }
    } catch (err) {
      console.error('Failed to load recorded games:', err);
      setRecordedError(err.message || 'Failed to load previous recorded games.');
      setRecordedGames([]);
    } finally {
      setIsLoadingRecorded(false);
    }
  }, [matchType, hasAccess]);

  useEffect(() => {
    loadRecordedGames();
  }, [loadRecordedGames]);

  useEffect(() => {
    if (!selectedMatchId || !selectedMatch?.homeTeam?.id || !selectedMatch?.awayTeam?.id) return;

    let cancelled = false;

    const loadPlayers = async () => {
      setIsLoadingPlayers(true);
      try {
        const homeId = selectedMatch.homeTeam.id;
        const awayId = selectedMatch.awayTeam.id;

        if (matchType === 'tournament') {
          const [homePlayers, awayPlayers] = await Promise.all([
            regionalTeamRosterService.getPlayers(homeId),
            regionalTeamRosterService.getPlayers(awayId),
          ]);
          if (cancelled) return;
          setPlayersByTeam({
            [homeId]: (homePlayers || []).map(mapRegionalPlayerToRoster),
            [awayId]: (awayPlayers || []).map(mapRegionalPlayerToRoster),
          });
        } else {
          const [homePlayers, awayPlayers] = await Promise.all([
            playerService.getByTeam(homeId),
            playerService.getByTeam(awayId),
          ]);
          if (cancelled) return;
          setPlayersByTeam({
            [homeId]: (homePlayers || []).map(mapLeaguePlayerToRoster),
            [awayId]: (awayPlayers || []).map(mapLeaguePlayerToRoster),
          });
        }
      } catch (err) {
        console.error('Failed to load players:', err);
        if (!cancelled) {
          showBanner('error', err.message || 'Failed to load team rosters.');
          setPlayersByTeam({});
        }
      } finally {
        if (!cancelled) setIsLoadingPlayers(false);
      }
    };

    loadPlayers();
    return () => {
      cancelled = true;
    };
    // Only reload when the selected game identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatchId, matchType]);

  // Live clock — regulation only; overtime has no time limit
  useEffect(() => {
    if (selectedMatch?.gameState?.status !== 'live') return undefined;
    if (isOvertimePeriod(selectedMatch?.gameState?.period)) return undefined;
    if (selectedMatch?.gameState?.timeRemaining == null) return undefined;

    const intervalId = window.setInterval(() => {
      setSelectedMatch((prev) => {
        if (!prev || prev.gameState.status !== 'live') return prev;
        if (isOvertimePeriod(prev.gameState.period) || prev.gameState.timeRemaining == null) {
          return prev;
        }
        const nextTime = Math.max(0, (prev.gameState.timeRemaining || 0) - 1);
        if (nextTime === 0) {
          pushEvent({
            type: 'period',
            description: `Clock expired — ${formatPeriodLabel(prev.gameState.period)} paused`,
            team: null,
          });
          return {
            ...prev,
            gameState: {
              ...prev.gameState,
              timeRemaining: 0,
              status: prev.gameState.period === 2 ? 'halftime' : 'scheduled',
            },
          };
        }
        return {
          ...prev,
          gameState: { ...prev.gameState, timeRemaining: nextTime },
        };
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [
    selectedMatch?.gameState?.status,
    selectedMatch?.gameState?.period,
    selectedMatch?.gameState?.timeRemaining,
    pushEvent,
  ]);

  const gameOptions = useMemo(
    () =>
      scheduledGames.map((game) => {
        if (matchType === 'tournament') {
          const home = game.homeTeam?.teamName || 'Home';
          const away = game.awayTeam?.teamName || 'Away';
          return {
            value: String(game.id),
            label: `${home} vs ${away} · ${formatScheduleLabel(game.scheduledDate)}`,
          };
        }
        const home = game.homeTeam?.shortName || game.homeTeam?.name || 'Home';
        const away = game.awayTeam?.shortName || game.awayTeam?.name || 'Away';
        return {
          value: String(game.id),
          label: `${home} vs ${away} · ${formatScheduleLabel(game.scheduledDate)}`,
        };
      }),
    [scheduledGames, matchType]
  );

  const gameStats = useMemo(() => {
    if (!selectedMatch) {
      return { home: aggregateTeamStats(), away: aggregateTeamStats() };
    }
    const home = aggregateTeamStats(playersByTeam[selectedMatch.homeTeam.id] || []);
    const away = aggregateTeamStats(playersByTeam[selectedMatch.awayTeam.id] || []);

    // Recorded finals may not have in-session player tallies — show final scores in team stats
    if (
      selectedMatch.gameState?.status === 'final' &&
      home.points === 0 &&
      away.points === 0 &&
      ((selectedMatch.homeTeam.score || 0) > 0 || (selectedMatch.awayTeam.score || 0) > 0)
    ) {
      return {
        home: { ...home, points: selectedMatch.homeTeam.score || 0 },
        away: { ...away, points: selectedMatch.awayTeam.score || 0 },
      };
    }

    return { home, away };
  }, [playersByTeam, selectedMatch]);

  const handleMatchTypeChange = (value) => {
    setMatchType(value);
  };

  const handleGameChange = (value) => {
    setSelectedMatchId(value);
    const game = scheduledGames.find((item) => String(item.id) === String(value));
    const shaped = toLiveMatchShape(game, matchType);
    setSelectedMatch(shaped);
    setGameEvents([]);
    setPlayersByTeam({});
    setHasStarted(shaped?.gameState?.status === 'live');
    startedAtRef.current =
      shaped?.gameState?.status === 'live' ? new Date().toISOString() : null;
  };

  const handleViewRecordedGame = useCallback(
    (game) => {
      if (hasStarted && !isFinal) {
        showBanner('error', 'Finish or leave the current live game before viewing past results.');
        return;
      }
      const shaped = toLiveMatchShape(game, matchType);
      setSelectedMatchId(String(game.id));
      setSelectedMatch(shaped);
      setGameEvents([]);
      setPlayersByTeam({});
      setHasStarted(false);
      startedAtRef.current = null;
      showBanner('success', 'Viewing recorded game stats (read-only).');
    },
    [hasStarted, isFinal, matchType, showBanner]
  );

  const updateMatch = useCallback((updater, { sync = false } = {}) => {
    setSelectedMatch((prev) => {
      if (!prev || prev.gameState.status === 'final') return prev;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (sync) {
        window.setTimeout(() => scheduleSync(), 0);
      }
      return next;
    });
  }, [scheduleSync]);

  const handleStartStop = useCallback(async () => {
    const match = selectedMatchRef.current;
    if (!match || match.gameState.status === 'final') return;

    if (match.gameState.status === 'live') {
      updateMatch((prev) => ({
        ...prev,
        gameState: {
          ...prev.gameState,
          status: prev.gameState.period === 2 ? 'halftime' : 'scheduled',
        },
      }));
      pushEvent({ type: 'timeout', description: 'Clock paused', team: null });
      scheduleSync();
      return;
    }

    const startedAt = startedAtRef.current || new Date().toISOString();
    const isFirstStart = !startedAtRef.current;
    startedAtRef.current = startedAt;
    setHasStarted(true);

    const next = {
      ...match,
      gameState: { ...match.gameState, status: 'live' },
    };
    setSelectedMatch(next);
    pushEvent({ type: 'timeout', description: isFirstStart ? 'Game started' : 'Clock resumed', team: null });

    if (isFirstStart && match.matchType === 'league') {
      try {
        await matchService.updateScore(match.id, {
          homeScore: match.homeTeam.score,
          awayScore: match.awayTeam.score,
          quarter: match.gameState.period,
          timeRemaining: formatClock(match.gameState.timeRemaining),
          matchStatus: 'live',
          startedAt,
        });
      } catch (err) {
        showBanner('error', err.message || 'Failed to mark match live.');
      }
    } else if (isFirstStart && match.matchType === 'tournament') {
      try {
        await tournamentService.updateGame(match.id, {
          homeScore: match.homeTeam.score,
          awayScore: match.awayTeam.score,
          gameStatus: 'live',
        });
      } catch (err) {
        showBanner('error', err.message || 'Failed to mark game live.');
      }
    } else {
      scheduleSync();
    }
  }, [pushEvent, scheduleSync, showBanner, updateMatch]);

  const startOvertime = useCallback(
    (fromPeriod) => {
      const nextPeriod = (fromPeriod || 4) + 1;
      const otNumber = nextPeriod - 4;
      pushEvent({
        type: 'period',
        description: `${formatPeriodLabel(nextPeriod)} starting (unlimited clock)`,
        team: null,
      });
      updateMatch(
        (prev) => ({
          ...prev,
          homeTeam: {
            ...prev.homeTeam,
            quarterScores: ensureQuarterScores(prev.homeTeam.quarterScores, nextPeriod),
          },
          awayTeam: {
            ...prev.awayTeam,
            quarterScores: ensureQuarterScores(prev.awayTeam.quarterScores, nextPeriod),
          },
          gameState: {
            ...prev.gameState,
            period: nextPeriod,
            timeRemaining: null,
            status: 'scheduled',
          },
        }),
        { sync: true }
      );
      showBanner(
        'info',
        `${formatPeriodLabel(nextPeriod)} ready — clock is open-ended. Press Start when ready.`
      );
      return otNumber;
    },
    [pushEvent, showBanner, updateMatch]
  );

  const handlePeriodChange = useCallback(() => {
    const match = selectedMatchRef.current;
    if (!match || match.gameState.status === 'final') return;

    const period = match.gameState.period;
    const tied = (match.homeTeam.score || 0) === (match.awayTeam.score || 0);

    if (period >= 4) {
      if (tied) {
        const goOt = window.confirm(
          `Score is tied ${match.homeTeam.score}-${match.awayTeam.score} after ${formatPeriodLabel(period)}. Start overtime with unlimited time?`
        );
        if (goOt) startOvertime(period);
        else showBanner('info', 'Use End Game to finalize, or Start Overtime when ready.');
        return;
      }

      const goOt = window.confirm(
        `Score is ${match.homeTeam.score}-${match.awayTeam.score} (not tied). Start overtime with unlimited time anyway?`
      );
      if (goOt) startOvertime(period);
      else showBanner('info', 'Use End Game to finalize the result.');
      return;
    }

    pushEvent({
      type: 'period',
      description: `Period advanced to ${formatPeriodLabel(period + 1)}`,
      team: null,
    });
    updateMatch(
      (prev) => ({
        ...prev,
        gameState: {
          ...prev.gameState,
          period: prev.gameState.period + 1,
          timeRemaining: PERIOD_LENGTH,
          status: prev.gameState.period === 2 ? 'halftime' : 'scheduled',
        },
      }),
      { sync: true }
    );
  }, [pushEvent, showBanner, startOvertime, updateMatch]);

  const handleEndPeriod = useCallback(() => {
    const match = selectedMatchRef.current;
    if (!match || match.gameState.status === 'final') return;

    const period = match.gameState.period;
    const tied = (match.homeTeam.score || 0) === (match.awayTeam.score || 0);

    pushEvent({
      type: 'period',
      description: `End of ${formatPeriodLabel(period)}`,
      team: null,
    });

    if (period >= 4) {
      if (tied) {
        const goOt = window.confirm(
          `End of ${formatPeriodLabel(period)} — game is tied ${match.homeTeam.score}-${match.awayTeam.score}. Start overtime (unlimited clock)?`
        );
        if (goOt) {
          startOvertime(period);
        } else {
          showBanner('info', 'Confirm End Game when ready to finalize.');
          updateMatch((prev) => ({
            ...prev,
            gameState: { ...prev.gameState, timeRemaining: isOvertimePeriod(period) ? null : 0, status: 'scheduled' },
          }));
        }
        return;
      }

      const goOt = window.confirm(
        `End of ${formatPeriodLabel(period)}. Score ${match.homeTeam.score}-${match.awayTeam.score}. Start another overtime?`
      );
      if (goOt) {
        startOvertime(period);
      } else {
        showBanner('info', 'Confirm End Game when ready to finalize.');
        updateMatch((prev) => ({
          ...prev,
          gameState: {
            ...prev.gameState,
            timeRemaining: isOvertimePeriod(period) ? null : 0,
            status: 'scheduled',
          },
        }));
      }
      return;
    }

    updateMatch(
      (prev) => ({
        ...prev,
        gameState: {
          ...prev.gameState,
          period: prev.gameState.period + 1,
          timeRemaining: PERIOD_LENGTH,
          status: prev.gameState.period === 2 ? 'halftime' : 'scheduled',
        },
      }),
      { sync: true }
    );
  }, [pushEvent, showBanner, startOvertime, updateMatch]);

  const handleTimeout = useCallback(
    (side) => {
      const match = selectedMatchRef.current;
      if (!match || match.gameState.status === 'final') return;
      const teamKey = side === 'home' ? 'homeTeam' : 'awayTeam';
      if ((match[teamKey]?.timeouts || 0) <= 0) return;

      updateMatch((prev) => ({
        ...prev,
        [teamKey]: {
          ...prev[teamKey],
          timeouts: prev[teamKey].timeouts - 1,
        },
        gameState: {
          ...prev.gameState,
          status: prev.gameState.status === 'live' ? 'scheduled' : prev.gameState.status,
        },
      }));
      pushEvent({
        type: 'timeout',
        description: 'Timeout called',
        team: side === 'home' ? 'HOME' : 'AWAY',
      });
      scheduleSync();
    },
    [pushEvent, scheduleSync, updateMatch]
  );

  const findPlayerTeam = useCallback((playerId) => {
    const match = selectedMatchRef.current;
    const players = playersByTeamRef.current;
    if (!match) return null;
    if ((players[match.homeTeam.id] || []).some((p) => p.id === playerId)) {
      return { side: 'home', teamId: match.homeTeam.id, teamKey: 'homeTeam' };
    }
    if ((players[match.awayTeam.id] || []).some((p) => p.id === playerId)) {
      return { side: 'away', teamId: match.awayTeam.id, teamKey: 'awayTeam' };
    }
    return null;
  }, []);

  const handlePlayerAction = useCallback(
    (playerId, action, value = 1) => {
      const match = selectedMatchRef.current;
      if (!match || match.gameState.status === 'final') return;

      const loc = findPlayerTeam(playerId);
      if (!loc) return;

      const roster = playersByTeamRef.current[loc.teamId] || [];
      const idx = roster.findIndex((p) => p.id === playerId);
      if (idx < 0) return;

      const player = { ...roster[idx] };
      let pointsAdded = 0;
      let description = '';

      switch (action) {
        case 'twoPointMade':
        case 'fieldGoal': {
          // fieldGoal kept for backward compatibility (value 2 or 3)
          if (action === 'fieldGoal' && value === 3) {
            pointsAdded = 3;
            player.points += 3;
            player.fieldGoalsMade += 1;
            player.fieldGoalsAttempted += 1;
            player.threePointersMade += 1;
            player.threePointersAttempted += 1;
            description = `${player.name} makes 3PT`;
            break;
          }
          pointsAdded = 2;
          player.points += 2;
          player.fieldGoalsMade += 1;
          player.fieldGoalsAttempted += 1;
          description = `${player.name} makes 2PT`;
          break;
        }
        case 'twoPointMiss':
          player.fieldGoalsAttempted += 1;
          description = `${player.name} misses 2PT`;
          break;
        case 'threePointMade':
          pointsAdded = 3;
          player.points += 3;
          player.fieldGoalsMade += 1;
          player.fieldGoalsAttempted += 1;
          player.threePointersMade += 1;
          player.threePointersAttempted += 1;
          description = `${player.name} makes 3PT`;
          break;
        case 'threePointMiss':
          player.fieldGoalsAttempted += 1;
          player.threePointersAttempted += 1;
          description = `${player.name} misses 3PT`;
          break;
        case 'freeThrowMade':
        case 'freeThrow': {
          pointsAdded = 1;
          player.points += 1;
          player.freeThrowsMade += 1;
          player.freeThrowsAttempted += 1;
          description = `${player.name} makes FT`;
          break;
        }
        case 'freeThrowMiss':
          player.freeThrowsAttempted += 1;
          description = `${player.name} misses FT`;
          break;
        case 'rebound':
          player.rebounds += 1;
          description = `${player.name} rebound`;
          break;
        case 'assist':
          player.assists += 1;
          description = `${player.name} assist`;
          break;
        case 'foul':
          player.fouls += 1;
          description = `${player.name} foul`;
          break;
        case 'steal':
          player.steals += 1;
          description = `${player.name} steal`;
          break;
        case 'block':
          player.blocks += 1;
          description = `${player.name} block`;
          break;
        case 'turnover':
          player.turnovers += 1;
          description = `${player.name} turnover`;
          break;
        default:
          return;
      }

      const nextRoster = [...roster];
      nextRoster[idx] = player;
      setPlayersByTeam((prev) => ({ ...prev, [loc.teamId]: nextRoster }));

      const periodIdx = Math.max(0, (match.gameState.period || 1) - 1);
      if (pointsAdded > 0 || action === 'foul') {
        updateMatch((m) => {
          const team = { ...m[loc.teamKey] };
          if (pointsAdded > 0) {
            team.score = (team.score || 0) + pointsAdded;
            const qs = ensureQuarterScores(team.quarterScores, match.gameState.period);
            qs[periodIdx] = (qs[periodIdx] || 0) + pointsAdded;
            team.quarterScores = qs;
          }
          if (action === 'foul') {
            team.teamFouls = (team.teamFouls || 0) + 1;
          }
          return { ...m, [loc.teamKey]: team };
        }, { sync: pointsAdded > 0 });
      }

      pushEvent({
        type: action === 'foul' ? 'foul' : pointsAdded > 0 ? 'score' : action,
        description,
        team: loc.side === 'home' ? 'HOME' : 'AWAY',
        player: player.name,
      });
    },
    [findPlayerTeam, pushEvent, updateMatch]
  );

  const handleSubstitution = useCallback(
    (playerId) => {
      const match = selectedMatchRef.current;
      if (!match || match.gameState.status === 'final') return;

      const loc = findPlayerTeam(playerId);
      if (!loc) return;

      setPlayersByTeam((prev) => {
        const roster = [...(prev[loc.teamId] || [])];
        const idx = roster.findIndex((p) => p.id === playerId);
        if (idx < 0) return prev;
        const player = roster[idx];
        const activeCount = roster.filter((p) => p.isActive).length;

        if (player.isActive) {
          roster[idx] = { ...player, isActive: false };
          pushEvent({
            type: 'substitution',
            description: `${player.name} to bench`,
            team: loc.side === 'home' ? 'HOME' : 'AWAY',
            player: player.name,
          });
          return { ...prev, [loc.teamId]: roster };
        }

        if (activeCount >= 5) {
          showBanner('error', 'Already 5 players on court. Sub someone out first.');
          return prev;
        }

        roster[idx] = { ...player, isActive: true };
        pushEvent({
          type: 'substitution',
          description: `${player.name} enters game`,
          team: loc.side === 'home' ? 'HOME' : 'AWAY',
          player: player.name,
        });
        return { ...prev, [loc.teamId]: roster };
      });
    },
    [findPlayerTeam, pushEvent, showBanner]
  );

  const handleEndGame = useCallback(async () => {
    const match = selectedMatchRef.current;
    if (!match || match.gameState.status === 'final' || isFinalizing) return;

    setIsFinalizing(true);
    const endedAt = new Date().toISOString();

    try {
      if (match.matchType === 'league') {
        await matchService.updateScore(match.id, {
          homeScore: match.homeTeam.score,
          awayScore: match.awayTeam.score,
          quarter: match.gameState.period,
          timeRemaining: formatClock(match.gameState.timeRemaining),
          matchStatus: 'completed',
          startedAt: startedAtRef.current || undefined,
          endedAt,
        });

        try {
          await matchService.addEvent(match.id, {
            teamId: match.homeTeam.id,
            eventType: 'quarter_end',
            quarter: match.gameState.period,
            timeRemaining: formatClock(match.gameState.timeRemaining),
            points: 0,
            description: `Final: ${match.homeTeam.shortName} ${match.homeTeam.score} - ${match.awayTeam.score} ${match.awayTeam.shortName}`,
          });
        } catch (eventErr) {
          console.warn('Final event save failed:', eventErr);
        }

        const leagueId = match.leagueId || match.source?.leagueId;
        if (leagueId) {
          const allPlayers = [
            ...(playersByTeamRef.current[match.homeTeam.id] || []),
            ...(playersByTeamRef.current[match.awayTeam.id] || []),
          ];
          await Promise.all(
            allPlayers.map((player) =>
              matchService.upsertPlayerMatchStats(player.id, leagueId, {
                gamesPlayed: 1,
                points: player.points || 0,
                rebounds: player.rebounds || 0,
                assists: player.assists || 0,
                steals: player.steals || 0,
                blocks: player.blocks || 0,
                fouls: player.fouls || 0,
                turnovers: player.turnovers || 0,
                fieldGoalsMade: player.fieldGoalsMade || 0,
                fieldGoalsAttempted: player.fieldGoalsAttempted || 0,
                threePointersMade: player.threePointersMade || 0,
                threePointersAttempted: player.threePointersAttempted || 0,
                freeThrowsMade: player.freeThrowsMade || 0,
                freeThrowsAttempted: player.freeThrowsAttempted || 0,
              })
            )
          );
        }
      } else {
        await tournamentService.updateGame(match.id, {
          homeScore: match.homeTeam.score,
          awayScore: match.awayTeam.score,
          gameStatus: 'completed',
        });

        const existingStats = await tournamentService.getRegionalPlayerStatistics();
        const byId = Object.fromEntries((existingStats || []).map((s) => [s.playerId, s]));
        const allPlayers = [
          ...(playersByTeamRef.current[match.homeTeam.id] || []),
          ...(playersByTeamRef.current[match.awayTeam.id] || []),
        ];

        await Promise.all(
          allPlayers.map((player) => {
            const cur = byId[player.id] || {};
            return tournamentService.upsertPlayerStatistics(player.id, {
              gamesPlayed: (cur.gamesPlayed || 0) + 1,
              points: (cur.points || 0) + (player.points || 0),
              rebounds: (cur.rebounds || 0) + (player.rebounds || 0),
              assists: (cur.assists || 0) + (player.assists || 0),
              steals: (cur.steals || 0) + (player.steals || 0),
              blocks: (cur.blocks || 0) + (player.blocks || 0),
            });
          })
        );
      }

      setSelectedMatch((prev) =>
        prev
          ? {
              ...prev,
              gameState: { ...prev.gameState, status: 'final' },
            }
          : prev
      );
      pushEvent({
        type: 'period',
        description: `Final — ${match.homeTeam.shortName} ${match.homeTeam.score} · ${match.awayTeam.score} ${match.awayTeam.shortName}`,
        team: null,
      });
      showBanner(
        'success',
        `Game finalized: ${match.homeTeam.shortName} ${match.homeTeam.score} - ${match.awayTeam.score} ${match.awayTeam.shortName}`
      );
      loadRecordedGames();
    } catch (err) {
      console.error('Failed to end game:', err);
      showBanner('error', err.message || 'Failed to finalize game.');
    } finally {
      setIsFinalizing(false);
    }
  }, [isFinalizing, loadRecordedGames, pushEvent, showBanner]);

  const handleQuickAction = useCallback(
    (actionId) => {
      const match = selectedMatchRef.current;
      if (!match || match.gameState.status === 'final') return;

      switch (actionId) {
        case 'endPeriod':
          handleEndPeriod();
          break;
        case 'endGame':
          handleEndGame();
          break;
        case 'startOvertime': {
          const period = match.gameState.period;
          if (period < 4) {
            showBanner('error', 'Overtime is available after the 4th quarter.');
            break;
          }
          const tied = (match.homeTeam.score || 0) === (match.awayTeam.score || 0);
          const confirmMsg = tied
            ? `Score tied ${match.homeTeam.score}-${match.awayTeam.score}. Start ${formatPeriodLabel(period + 1)} with unlimited time?`
            : `Score is ${match.homeTeam.score}-${match.awayTeam.score}. Start ${formatPeriodLabel(period + 1)} with unlimited time anyway?`;
          if (window.confirm(confirmMsg)) startOvertime(period);
          break;
        }
        case 'technicalFoul': {
          updateMatch((prev) => ({
            ...prev,
            homeTeam: {
              ...prev.homeTeam,
              teamFouls: (prev.homeTeam.teamFouls || 0) + 1,
            },
          }));
          pushEvent({
            type: 'technical',
            description: 'Technical foul',
            team: 'HOME',
          });
          break;
        }
        case 'officialTimeout':
          updateMatch((prev) => ({
            ...prev,
            gameState: {
              ...prev.gameState,
              status: prev.gameState.status === 'live' ? 'scheduled' : prev.gameState.status,
            },
          }));
          pushEvent({ type: 'timeout', description: 'Official timeout', team: null });
          scheduleSync();
          break;
        case 'resetClock':
          updateMatch((prev) => ({
            ...prev,
            gameState: {
              ...prev.gameState,
              timeRemaining: getClockForPeriod(prev.gameState.period),
            },
          }));
          pushEvent({
            type: 'timeout',
            description: isOvertimePeriod(match.gameState.period)
              ? 'OT clock reset (unlimited)'
              : 'Clock reset to 10:00',
            team: null,
          });
          scheduleSync();
          break;
        case 'adjustScore': {
          const homeInput = window.prompt('Home score', String(match.homeTeam.score));
          if (homeInput === null) break;
          const awayInput = window.prompt('Away score', String(match.awayTeam.score));
          if (awayInput === null) break;
          const homeScore = parseInt(homeInput, 10);
          const awayScore = parseInt(awayInput, 10);
          if (!Number.isFinite(homeScore) || !Number.isFinite(awayScore)) {
            showBanner('error', 'Invalid scores entered.');
            break;
          }
          updateMatch(
            (prev) => ({
              ...prev,
              homeTeam: { ...prev.homeTeam, score: homeScore },
              awayTeam: { ...prev.awayTeam, score: awayScore },
            }),
            { sync: true }
          );
          pushEvent({
            type: 'score',
            description: `Score adjusted to ${homeScore}-${awayScore}`,
            team: null,
          });
          break;
        }
        case 'reviewPlay':
          pushEvent({ type: 'review', description: 'Official review initiated', team: null });
          break;
        default:
          break;
      }
    },
    [
      handleEndGame,
      handleEndPeriod,
      pushEvent,
      scheduleSync,
      showBanner,
      startOvertime,
      updateMatch,
    ]
  );

  const handleAddEvent = (event) => {
    setGameEvents((prev) => [event, ...prev]);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!selectedMatchRef.current || selectedMatchRef.current.gameState.status === 'final') return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;

      if (e.code === 'Space') {
        e.preventDefault();
        handleStartStop();
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        handleTimeout('home');
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        pushEvent({ type: 'foul', description: 'Foul noted (keyboard)', team: 'HOME' });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleStartStop, handleTimeout, pushEvent]);

  useEffect(
    () => () => {
      if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    },
    []
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="ShieldAlert" size={48} className="text-warning mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/admin-dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const matchSelectionHeader = (
    <div className="mb-6 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Match Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Select league or tournament, then choose a scheduled game
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full lg:w-auto lg:min-w-[560px]">
          <Select
            label="Match Type"
            options={MATCH_TYPE_OPTIONS}
            value={matchType}
            onChange={handleMatchTypeChange}
            placeholder="Select match type"
            disabled={hasStarted && !isFinal}
          />
          <Select
            label="Scheduled Game"
            options={gameOptions}
            value={selectedMatchId}
            onChange={handleGameChange}
            placeholder={
              isLoadingGames
                ? 'Loading schedule...'
                : gameOptions.length === 0
                  ? 'No scheduled games'
                  : 'Select game by schedule'
            }
            searchable
            disabled={isLoadingGames || gameOptions.length === 0 || (hasStarted && !isFinal)}
          />
        </div>
      </div>
      {gamesError && (
        <div className="px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
          {gamesError}
        </div>
      )}
      {banner && (
        <div
          className={`px-4 py-3 rounded-lg border text-sm ${
            banner.type === 'success'
              ? 'bg-success/10 border-success/20 text-success'
              : banner.type === 'error'
                ? 'bg-destructive/10 border-destructive/20 text-destructive'
                : 'bg-primary/10 border-primary/20 text-primary'
          }`}
        >
          {banner.message}
        </div>
      )}
      {isFinal && (
        <div className="px-4 py-3 rounded-lg border bg-muted border-border text-muted-foreground text-sm">
          This game is finalized. Scoring and edits are locked.
        </div>
      )}
    </div>
  );

  if (!selectedMatch) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex">
          <Sidebar
            isCollapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
          <main
            className={`flex-1 transition-all duration-300 ${
              sidebarCollapsed ? 'ml-16' : 'ml-72'
            } mt-16`}
          >
            <div className="p-6 space-y-6">
              <Breadcrumb />
              {matchSelectionHeader}
              <div className="text-center py-12 bg-card rounded-lg border border-border card-shadow">
                {isLoadingGames ? (
                  <>
                    <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
                    <p className="text-muted-foreground">Loading scheduled games...</p>
                  </>
                ) : (
                  <>
                    <Icon name="Calendar" size={64} className="text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-2xl font-semibold text-foreground mb-2">
                      {gameOptions.length === 0 ? 'No Scheduled Games' : 'Select a Scheduled Game'}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      {gameOptions.length === 0
                        ? `No ${matchType === 'tournament' ? 'tournament' : 'league'} games are available on the schedule yet.`
                        : 'Choose a game from the schedule dropdown above to open live match management.'}
                    </p>
                    {matchType === 'league' ? (
                      <Button
                        variant="default"
                        onClick={() => navigate('/league-organizer')}
                        iconName="Plus"
                        iconPosition="left"
                      >
                        Open League Organizer
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        onClick={() => navigate('/tournament-games-organizer')}
                        iconName="Plus"
                        iconPosition="left"
                      >
                        Open Games Organizer
                      </Button>
                    )}
                  </>
                )}
              </div>

              <PreviousRecordedGames
                matchType={matchType}
                games={recordedGames}
                loading={isLoadingRecorded}
                error={recordedError}
                selectedGameId={selectedMatchId}
                onSelectGame={handleViewRecordedGame}
                onRetry={loadRecordedGames}
              />
            </div>
          </main>
        </div>
      </div>
    );
  }

  const homePlayers = playersByTeam[selectedMatch.homeTeam.id] || [];
  const awayPlayers = playersByTeam[selectedMatch.awayTeam.id] || [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? 'ml-16' : 'ml-72'
          } mt-16`}
        >
          <div className="p-6">
            <Breadcrumb />
            {matchSelectionHeader}

            {isLoadingPlayers && (
              <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Loader2" size={16} className="animate-spin" />
                Loading rosters...
              </div>
            )}

            <div className={`grid grid-cols-1 xl:grid-cols-4 gap-6 ${isFinal ? 'opacity-90' : ''}`}>
              <div className="xl:col-span-3 space-y-6">
                <GameScoreboard
                  homeTeam={selectedMatch.homeTeam}
                  awayTeam={selectedMatch.awayTeam}
                  gameState={selectedMatch.gameState}
                  onTimeoutHome={() => handleTimeout('home')}
                  onTimeoutAway={() => handleTimeout('away')}
                  onStartStop={handleStartStop}
                  onPeriodChange={handlePeriodChange}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <PlayerRoster
                    team={selectedMatch.homeTeam}
                    players={homePlayers}
                    onPlayerAction={isFinal ? () => {} : handlePlayerAction}
                    onSubstitution={isFinal ? () => {} : handleSubstitution}
                    isHomeTeam
                  />
                  <PlayerRoster
                    team={selectedMatch.awayTeam}
                    players={awayPlayers}
                    onPlayerAction={isFinal ? () => {} : handlePlayerAction}
                    onSubstitution={isFinal ? () => {} : handleSubstitution}
                    isHomeTeam={false}
                  />
                </div>

                <StatisticsPanel
                  homeTeam={selectedMatch.homeTeam}
                  awayTeam={selectedMatch.awayTeam}
                  gameStats={gameStats}
                  homePlayers={homePlayers}
                  awayPlayers={awayPlayers}
                  matchMeta={{
                    matchType: selectedMatch.matchType,
                    status: selectedMatch.gameState?.status,
                    period: selectedMatch.gameState?.period,
                  }}
                />
              </div>

              <div className="space-y-6">
                <QuickActions
                  onAction={handleQuickAction}
                  gameState={selectedMatch.gameState}
                  disabled={isFinal || isFinalizing}
                />
                <GameEventLog
                  events={gameEvents}
                  onAddEvent={isFinal ? () => {} : handleAddEvent}
                  gameState={selectedMatch.gameState}
                />
                <PreviousRecordedGames
                  matchType={matchType}
                  games={recordedGames}
                  loading={isLoadingRecorded}
                  error={recordedError}
                  selectedGameId={selectedMatchId}
                  onSelectGame={handleViewRecordedGame}
                  onRetry={loadRecordedGames}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MatchManagement;
